import type React from "react"
import type { SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useParams } from "react-router"

import { RootPath } from "@/models/route"
import { Form, FormTextField, SubmitButton, useForm } from "@/nzds"
import { useResendEmail, useValidateAccount } from "@/services/user"
import type { ResendEmailError } from "@/services/user"
import { APP_COMPANY_NAME } from "@/utils/constants"
import type { AxiosError } from "axios"

import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Fade from "@mui/material/Fade"
import Typography from "@mui/material/Typography"

import { If } from "../if/if"
import { VerifyEmail } from "../sign-up/verify-email/verify-email"
import { WhiteBox } from "../whiteBox"
import { reSendSchema } from "./re-send.schema"
import type { ReSendSchema } from "./re-send.schema"

const hasServerError = (
  error: AxiosError<ResendEmailError>,
  regex: RegExp
): boolean => {
  return Boolean(
    error.response?.data.errors.find((errorMessage) => regex.test(errorMessage))
  )
}

export const ValidateAccount: React.FC = () => {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()

  const {
    validateAccountData,
    validateAccountIsError,
    validateAccountIsSuccess,
  } = useValidateAccount(token ?? "")

  const form = useForm({
    schema: reSendSchema,
    defaultValues: {
      email: "",
    },
  })

  const { resendEmail, resendEmailIsPending, resendEmailIsSuccess } =
    useResendEmail({
      onError: (error): void => {
        const emailErrorMessage: string = hasServerError(error, /email/i)
          ? t("verifyAccount.invalidEmailError")
          : ""

        if (emailErrorMessage) {
          form.setError("root.emailServerError", {
            type: error.response?.statusText,
            message: emailErrorMessage,
          })
        }
      },
    })

  const handleSubmit: SubmitHandler<ReSendSchema> = (value): void => {
    void resendEmail(value.email)
  }

  return (
    <Fade in={validateAccountIsError || validateAccountIsSuccess}>
      <div>
        <If condition={validateAccountData?.data.success}>
          <WhiteBox aria-labelledby="verificationSuccessHeader">
            <Typography
              id="verificationSuccessHeader"
              variant="h1"
              sx={{
                mb: 4,
                textAlign: "center",
              }}
            >
              {t("verifyAccount.successTitle")}
            </Typography>
            <Box
              sx={{
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  mb: 4,
                }}
              >
                {t("verifyAccount.successMessage")}
              </Typography>
              <Button
                color="primary"
                component={RouterLink}
                to={RootPath.Root}
                variant="contained"
              >
                {t("verifyAccount.logInButton", {
                  companyName: APP_COMPANY_NAME,
                })}
              </Button>
            </Box>
          </WhiteBox>
        </If>

        <If condition={validateAccountIsError}>
          <If condition={!resendEmailIsSuccess}>
            <WhiteBox aria-labelledby="verificationErrorHeader">
              <Typography
                id="verificationErrorHeader"
                variant="h1"
                sx={{
                  mb: 4,
                  textAlign: "center",
                }}
              >
                {t("verifyAccount.failureTitle")}
              </Typography>
              <Typography
                sx={{
                  mb: 4,
                }}
              >
                {t("verifyAccount.failureMessage")}
              </Typography>
              <Form<ReSendSchema>
                aria-labelledby="verificationErrorHeader"
                form={form}
                onSubmit={handleSubmit}
              >
                <Box
                  sx={{
                    mb: 2,
                  }}
                >
                  <FormTextField
                    fullWidth
                    error={Boolean(
                      form.formState.errors.root?.emailServerError
                    )}
                    helperText={
                      form.formState.errors.root?.emailServerError?.message
                    }
                    id="email"
                    label={t("fields.email.title")}
                    name="email"
                    preserveHelperTextSpacing
                    variant="stacked"
                  />
                </Box>

                <SubmitButton
                  disabled={resendEmailIsPending}
                  fullWidth
                  loading={resendEmailIsPending}
                >
                  {t("verifyAccount.resendEmailButton")}
                </SubmitButton>
              </Form>
            </WhiteBox>
          </If>
          <If condition={resendEmailIsSuccess}>
            <WhiteBox aria-labelledby="verificationSentTitle">
              <Fade in={true}>
                <div>
                  <VerifyEmail email={form.getValues("email")} />
                </div>
              </Fade>
            </WhiteBox>
          </If>
        </If>
      </div>
    </Fade>
  )
}
